/**
 * Keyboard input → editor action.
 *
 * Kept pure so the bindings are testable without synthesizing DOM events, and so
 * the editor has exactly one place where a key becomes a mutation — the same
 * reason every mutation goes through `editorReducer`.
 *
 * These are handled by a local listener rather than `KeyboardShortcutsProvider`
 * because the provider suppresses every binding while ctrl/meta is held and while
 * focus is inside an input, which is precisely when undo/redo must work. The key
 * strings still come from `constants/shortcuts.ts`
 * (see docs/keyboard-shortcuts.md).
 */
import { EMAIL_EDITOR_SHORTCUTS } from '@/constants/shortcuts';
import { EditorAction } from './reducer';

/** The parts of a keyboard event the bindings depend on. */
export interface KeyStroke {
  key: string;
  metaKey?: boolean;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  /** True when the event originated in a text field the browser owns. */
  fromTextInput?: boolean;
}

export interface KeymapContext {
  selectedBlockId: string | null;
  /** No mutation is offered in read-only mode; navigation keys still are. */
  readOnly?: boolean;
}

/** ⌘ on macOS, Ctrl elsewhere — accept either rather than sniffing the platform. */
function isAccel(stroke: KeyStroke): boolean {
  return Boolean(stroke.metaKey || stroke.ctrlKey);
}

/**
 * The action a keystroke should dispatch, or null to let the event through.
 *
 * Returning null (rather than a no-op action) matters: the caller uses it to
 * decide whether to call `preventDefault`, and swallowing an unhandled key would
 * break browser and Ant Design behaviour inside the editor.
 */
export function editorActionForKey(
  stroke: KeyStroke,
  context: KeymapContext,
): EditorAction | null {
  const { key } = stroke;
  const lower = key.length === 1 ? key.toLowerCase() : key;

  // Escape clears the selection even in read-only mode, and must keep working
  // inside a text field — it is how you get *out* of one.
  if (key === 'Escape') {
    return context.selectedBlockId === null
      ? null
      : { type: 'selectBlock', blockId: null };
  }

  if (context.readOnly) return null;

  if (isAccel(stroke) && !stroke.altKey) {
    /**
     * Inside a text field the browser's own undo wins. Latent today — the shell
     * has no inputs yet — but the inspector (2.3) adds one per declared param, and
     * whether ⌘Z there should revert *typing* or the *document* depends on how the
     * inspector commits: with `setProps({coalesce: true})` per keystroke the
     * document undo is the better undo, and this guard should be relaxed for that
     * case. Until that is decided, native behaviour is the safe default — the bad
     * outcome is ⌘Z appearing to undo typing while actually reverting an unrelated
     * earlier edit.
     */
    if (stroke.fromTextInput) return null;

    if (lower === EMAIL_EDITOR_SHORTCUTS.UNDO) {
      return stroke.shiftKey ? { type: 'redo' } : { type: 'undo' };
    }
    // Ctrl+Y is the Windows redo; ⌘Y is a browser binding on macOS, so it is
    // only honoured with Ctrl.
    if (lower === EMAIL_EDITOR_SHORTCUTS.REDO && stroke.ctrlKey) {
      return { type: 'redo' };
    }
    if (lower === EMAIL_EDITOR_SHORTCUTS.DUPLICATE && context.selectedBlockId) {
      return { type: 'duplicateBlock', blockId: context.selectedBlockId };
    }
    return null;
  }

  // Delete is unmodified, so it must not fire while the user is editing text —
  // there it means "delete a character", not "delete the block".
  if (
    (key === 'Delete' || key === 'Backspace') &&
    !stroke.fromTextInput &&
    context.selectedBlockId
  ) {
    return { type: 'deleteBlock', blockId: context.selectedBlockId };
  }

  return null;
}

/** `<input>` types that hold free text the browser can undo. */
const TEXT_INPUT_TYPES = new Set([
  '',
  'text',
  'search',
  'email',
  'url',
  'tel',
  'password',
]);

/**
 * True only for a field that holds **free text with its own browser undo stack**.
 *
 * Deliberately narrower than "is an input". Ant Design's `Select` and
 * `InputNumber` keep focus in an inner `<input>` — a readonly `role="combobox"`
 * filter and a `role="spinbutton"` respectively — and treating those as text
 * fields is not a theoretical problem: it made ⌘Z silently stop working after
 * *every* dropdown or stepper interaction in the inspector, since the guard in
 * `editorActionForKey` then handed undo to a browser that had nothing to undo.
 * Found in the browser.
 */
export function isTextInputTarget(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  if (!element || typeof element.tagName !== 'string') return false;

  if (element.isContentEditable === true) return true;
  if (element.tagName === 'TEXTAREA') return true;
  if (element.tagName !== 'INPUT') return false;

  const input = element as HTMLInputElement;
  if (input.readOnly === true) return false;
  if (!TEXT_INPUT_TYPES.has((input.type || '').toLowerCase())) return false;

  // A widget that owns its own keyboard semantics, not a text field.
  const role = input.getAttribute('role');
  return role !== 'combobox' && role !== 'spinbutton';
}

/** Selector for anything the browser focuses on its own when clicked. */
const SELF_FOCUSING =
  'input, textarea, select, button, a[href], [contenteditable="true"], [tabindex]:not([tabindex="-1"])';

/**
 * True when a click on `target` will be focused by the browser itself.
 *
 * The editor claims focus on `mousedown` so its shortcuts work anywhere inside it
 * (see `VisualEditorBody`), and that has to yield to real controls: claiming during
 * `mousedown` on a code editor left focus on the shell and the keystrokes went to the
 * page — where the global two-key navigation shortcut picked them up and navigated
 * away mid-edit. Observed in the browser, twice, from the two halves of this problem.
 */
export function ownsItsOwnFocus(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  if (!element || typeof element.closest !== 'function') return false;
  return element.closest(SELF_FOCUSING) !== null;
}
